import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appSignInConfiguration", "connectionType", "features", "installUrl", "isDashworks", "isOpsHub", "isUngatedForSecureInstallFlow", "onClick", "onConnectionComplete", "onInstallComplete", "onInstallError", "previewListingId", "productFeatures", "slug", "sourceId"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { H2 } from 'foundations-components/transitional/components/H2';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useGetOAuthUrlQuery, useSendInstallIntentLazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import { useIsStandardizedInstallQuery } from '../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc-app.hooks';
import { useGetSatisfyingOptionsQuery } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { getConnectedAppsUrl, getFormattedUrl, navigateTo } from '../utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../utils/TrackingUtils';
import { getAppInstallType } from '../utils/getAppInstallType';
import { AppUninstallableMessage } from './AppUninstallableMessage';
import { RequestPermissionsModal } from './RequestPermissionsModal';
import { useAppStatus } from './hooks/useAppStatus';
import { useAppUninstallableReason } from './hooks/useAppUninstallableReason';
import { InstallWizard } from './installWizard/InstallWizard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-button' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-modal' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'data-sync-connect-button' */'ui-universal-auth/DataSyncAuthButton')
});
const AsyncDashworksAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'dashworks-auth-button' */'ui-universal-auth/DashworksAuthButton').then(module => module.DashworksAuthButton)
});
export function AppCTAButton(_ref) {
  let {
      appSignInConfiguration,
      connectionType,
      features,
      installUrl = '',
      isDashworks,
      isOpsHub,
      isUngatedForSecureInstallFlow,
      onClick,
      onConnectionComplete,
      onInstallComplete = () => {},
      onInstallError = () => {},
      previewListingId,
      productFeatures,
      slug,
      sourceId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const openCopilot = useOpenCopilot();
  const {
    appContext,
    iconUrl,
    isEmbedded,
    listingId,
    listingIdV2,
    name,
    productType,
    published,
    trackInstall,
    trackInteraction
  } = useDetailsPageContext();
  const [standardInstallModalOpen, setStandardInstallModalOpen] = useState(false);
  const [homemadeIframeModalOpen, setHomemadeIframeModalOpen] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const [sendInstallIntent] = useSendInstallIntentLazyQuery();
  const [dashworksInstalled, setDashworksInstalled] = useState(false);
  const isPreview = !published || !!previewListingId;
  const {
    data: standardizedInstallData
  } = useIsStandardizedInstallQuery({
    variables: {
      listingId: listingId || 0
    },
    skip: !listingId || appContext !== 'app' || productType !== 'APP' || connectionType !== 'STANDARD'
  });
  const isUngatedToStandardInstall = standardizedInstallData === null || standardizedInstallData === void 0 ? void 0 : standardizedInstallData.standardizedInstall;
  const isUIE = useMemo(() => {
    return !!(productFeatures !== null && productFeatures !== void 0 && productFeatures.some(feature => feature.type === 'UIE'));
  }, [productFeatures]);
  const installType = getAppInstallType({
    connectionType,
    isDashworks,
    isOpsHub,
    isUngatedToStandardInstall
  });
  const onInstallClickDefault = useCallback(() => {
    void sendInstallIntent({
      variables: {
        slug
      }
    });
    if (onClick) {
      onClick();
    }
    trackInstall({});
    trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
  }, [onClick, sendInstallIntent, slug, trackInstall]);

  // Do not use sourceId as a number for AGENT productType. Known issue with globalId size causing parseInt to incorrectly convert.
  const sourceIdAsNumber = sourceId ? typeof sourceId === 'number' ? sourceId : parseInt(sourceId, 10) : undefined;
  const {
    missingScopes,
    uninstallableReason,
    loading: installCompatibilityLoading
  } = useAppUninstallableReason({
    appContext,
    features,
    listingId,
    published
  });

  // Fetch app status for installed apps
  const isAppInstalled = (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED';
  const {
    statusData,
    statusLoading,
    statusError
  } = useAppStatus({
    appId: sourceIdAsNumber,
    shouldFetch: appContext === 'app' && isAppInstalled && !isPreview
  });
  const appStatusResult = statusData === null || statusData === void 0 ? void 0 : statusData.find(result => result.appId === sourceIdAsNumber);
  const {
    type: statusType
  } = appStatusResult || {};

  // Only show reconnect if status was fetched successfully and app is disconnected
  const shouldShowReconnect = isAppInstalled && !statusLoading && !statusError && statusType === 'disconnected';

  // Only request OAuth URL if app is STANDARD installType
  const {
    data: oauthUrlData,
    loading: oauthUrlLoading,
    refetch: refetchOAuthUrl
  } = useGetOAuthUrlQuery({
    variables: {
      listingId,
      published
    },
    skip: appContext !== 'app' || installType !== 'STANDARD'
  });
  const oauthUrl = oauthUrlData === null || oauthUrlData === void 0 ? void 0 : oauthUrlData.oauthUrl;
  useEffect(() => {
    if (appContext === 'app' && installType === 'STANDARD' && listingId) {
      void refetchOAuthUrl();
    }
  }, [appContext, installType, listingId, published, refetchOAuthUrl]);
  const {
    data: missingPlanData
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: missingScopes
    },
    skip: missingScopes.length === 0
  });
  const handleConnectionComplete = useCallback(payload => {
    if (onConnectionComplete) {
      // Analytics TO DO
      onConnectionComplete(payload);
    } else if (payload) {
      const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
      // In marketplace-ui-details, we might want to show a success message instead of redirecting
      if (redirectUrl) {
        navigateTo(redirectUrl, isEmbedded);
      } else {
        navigateTo(getConnectedAppsUrl(), isEmbedded);
      }
    }
    onInstallComplete();
  }, [isEmbedded, onConnectionComplete, onInstallComplete]);
  const handleConnectionFailed = useCallback(err => {
    var _err$meta, _err$meta2;
    const errorType = ((_err$meta = err.meta) === null || _err$meta === void 0 ? void 0 : _err$meta.type) || 'default';
    const errorMessage = (_err$meta2 = err.meta) === null || _err$meta2 === void 0 ? void 0 : _err$meta2.appErrorMessage;

    // For INTEGRATION_SPECIFIC_ERROR, use the errorMessage directly as the message
    if (errorType === 'INTEGRATION_SPECIFIC_ERROR' && errorMessage) {
      FloatingAlertStore.addAlert({
        message: errorMessage,
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    } else {
      // For all other error types, use the static translations
      FloatingAlertStore.addAlert({
        message: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.message`, {
          name
        }),
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    }
    onInstallError();
  }, [name, onInstallError]);

  // Determine which button to render
  let renderedButton;
  if (installCompatibilityLoading || oauthUrlLoading || statusLoading) {
    renderedButton = /*#__PURE__*/_jsx(LoadingButton, {
      disabled: true,
      loading: true,
      role: "button",
      size: "medium",
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    });
  }
  // Renders a "Request to install" button for missing user permissions
  else if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_USER_PERMISSIONS') {
    renderedButton = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        onClick: () => {
          setOpenPermissionsModal(true);
          trackInteraction({
            action: 'click request install app permission button'
          });
        },
        use: "primary"
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.request"
        })
      })), openPermissionsModal && /*#__PURE__*/_jsx(RequestPermissionsModal, {
        listingId: listingIdV2 !== null && listingIdV2 !== void 0 ? listingIdV2 : 0,
        missingUserRoles: uninstallableReason.label === 'MISSING_USER_PERMISSIONS' ? uninstallableReason.missingUserRoles : [],
        onClose: () => setOpenPermissionsModal(false)
      })]
    });
  } else if (dashworksInstalled || isDashworks && !isEmbedded && (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'ALREADY_INSTALLED') {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      onClick: () => {
        openCopilot();
      },
      role: "button",
      use: "primary"
    }, rest, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        textWrap: "wrap",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.openBreeze"
        })
      })
    }));
  }
  // Disabled or Trial button with tooltip for all other uninstallable reasons
  else if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  statusType !== 'disconnected' && uninstallableReason) {
    let tooltipContent = /*#__PURE__*/_jsx(AppUninstallableMessage, {
      uninstallableReason: uninstallableReason
    });
    const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
    if (uninstallableReason.label === 'MISSING_HUB_SCOPES' && missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1 && missingPlanOptions[0].and[0].or[0].displayName // skip if missing a display name
    ) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        tooltipContent = /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      } else {
        tooltipContent = /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
            options: {
              productType: unescapedText(`detailsPage.productType.${productType}`)
            }
          }), /*#__PURE__*/_jsx(List, {
            marginBlockStart: "200",
            marginInlineStart: "-400",
            styled: true,
            children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
                options: {
                  planName: option.displayName
                }
              })
            }, index))
          })]
        });
      }
    }
    renderedButton = /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipContent,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        disabled: true,
        onClick: () => {
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          trackInstall({});
        },
        role: "button",
        use: "primary"
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.APP"
        })
      }))
    });
  }
  // Button that renders standard InstallWizard for apps with configured OAuth URLs
  else if (installType === 'STANDARD' && oauthUrl) {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      onClick: () => {
        void sendInstallIntent({
          variables: {
            slug
          }
        });
        setStandardInstallModalOpen(true);
        trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
        trackInteraction({
          action: 'open install wizard'
        });
        trackInstall({});
      },
      use: shouldShowReconnect ? 'danger' : 'primary'
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
      })
    }));
  }
  // Simple button with href for LEGACY and HOMEMADE_EXTERNAL installTypes
  else if (installType === 'LEGACY' || installType === 'HOMEMADE_EXTERNAL') {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      external: installType === 'HOMEMADE_EXTERNAL' || isEmbedded,
      href: getFormattedUrl(installUrl),
      onClick: onInstallClickDefault,
      role: "button",
      use: shouldShowReconnect ? 'danger' : 'primary'
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
      })
    }));
  }
  // Data sync auth button
  else if (installType === 'HOMEMADE_DATA_SYNC') {
    renderedButton = /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, Object.assign({
      appId: sourceId !== null && sourceId !== void 0 ? sourceId : 0,
      onClick: onInstallClickDefault,
      onComplete: onInstallComplete,
      portalId: PortalIdParser.get(),
      trackingSource: "marketplace",
      use: shouldShowReconnect ? 'danger' : 'primary'
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
      })
    }));
  }
  // Dashworks auth button
  else if (installType === 'HOMEMADE_DASHWORKS') {
    renderedButton = /*#__PURE__*/_jsx(AsyncDashworksAuthButton, Object.assign({
      appId: sourceIdAsNumber !== null && sourceIdAsNumber !== void 0 ? sourceIdAsNumber : 0,
      onCancel: () => {
        trackInteraction({
          action: 'dashworks app connection cancelled'
        });
      },
      onClick: onInstallClickDefault,
      onComplete: __secretId => {
        trackInteraction({
          action: 'dashworks app connected'
        });
        onInstallComplete();
        setDashworksInstalled(true);
        if (!isEmbedded) {
          openCopilot();
        } else {
          window.location.reload();
        }
      },
      portalId: PortalIdParser.get(),
      use: shouldShowReconnect ? 'danger' : 'primary'
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
      })
    }));
  }
  // Iframe modal button for builtByHubSpot modal connections
  else if (installType === 'HOMEMADE_MODAL') {
    renderedButton = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        onClick: () => {
          void sendInstallIntent({
            variables: {
              slug
            }
          });
          setHomemadeIframeModalOpen(true);
          trackInstall({});
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          // Add analytics event
        },
        use: shouldShowReconnect ? 'danger' : 'primary'
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
        })
      })), homemadeIframeModalOpen && /*#__PURE__*/_jsxs(Modal, {
        use: "success",
        width: 600,
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: () => setHomemadeIframeModalOpen(false)
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP'
            })
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          paddingBlockEnd: "400",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            appId: sourceId !== null && sourceId !== void 0 ? sourceId : listingId,
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('Integration not found');
            },
            portalId: PortalIdParser.get(),
            sourceAppId: "app-marketplace",
            text: I18n.text(shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP')
          })
        })]
      })]
    });
  }
  // Default integrations connect button for builtByHubSpot iframe
  else if (installType === 'HOMEMADE_IFRAME') {
    renderedButton = /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, {
        use: shouldShowReconnect ? 'danger' : 'primary',
        "data-test-id": "cta-button",
        appId: sourceId !== null && sourceId !== void 0 ? sourceId : 0,
        fullWidth: true,
        onClick: onInstallClickDefault,
        onConnectionComplete: handleConnectionComplete,
        onConnectionFailed: handleConnectionFailed,
        portalId: PortalIdParser.get(),
        sourceAppId: "app-marketplace",
        text: I18n.text(shouldShowReconnect ? 'detailsPage.productDetailsCard.cta.reconnect' : 'detailsPage.productDetailsCard.cta.install.APP')
      })
    });
  }
  // Fallback button
  else {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      disabled: true,
      use: "primary"
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    }));
  }
  const oauthQueryParams = oauthUrl ? new URL(oauthUrl).search : '';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedButton, standardInstallModalOpen && /*#__PURE__*/_jsx(InstallWizard, {
      appSignInConfiguration: appSignInConfiguration,
      appId: String(sourceId !== null && sourceId !== void 0 ? sourceId : ''),
      features: features,
      iconUrl: iconUrl,
      installUrl: installUrl,
      isPreview: isPreview,
      isUIE: isUIE,
      isUngatedForSecureInstallFlow: isUngatedForSecureInstallFlow,
      listingId: listingId,
      name: name,
      oauthQueryParams: oauthQueryParams,
      onClose: () => {
        trackInteraction({
          action: 'close install wizard'
        });
        setStandardInstallModalOpen(false);
      },
      portalId: PortalIdParser.get()
    })]
  });
}